import {createSlice, createAsyncThunk} from '@reduxjs/toolkit';
import {setLocalData, getLocalData} from "../../services/storageManager";
import Constants from "../../utils/constants";
import apiClient from "../../services/apiClient";
import toast from "../../utils/toast";

const configData = getLocalData('configData');
const configList = getLocalData('configList');

const initialState = {
    accessToken: '',
    teams: [],
    config: configData ? JSON.parse(configData) : {},
    configList: configList ? JSON.parse(configList) : [],
    spaces: {},
    folders: {},
    lists: {},
    loading: false,
    error: null,
};

export const fetchAccessToken = createAsyncThunk(
    'getToken',
    async (data, thunkAPI) => {

        return await apiClient({
            url: `${Constants.API.BASE_URL}${Constants.API.TOKEN.GET}/${data.clientID}/${data.clientSecret}/${data.code}`,
            method: 'GET'
        }, true)
            .then((data) => data.data)
            .catch((error) => {
                return thunkAPI.rejectWithValue(error)
            })
    }
);
export const fetchTeams = createAsyncThunk(
    'getTeams',
    async (token, thunkAPI) => {

        return await apiClient({
            url: `${Constants.API.BASE_URL}${Constants.API.FETCH_TEAMS}`,
            method: 'GET',
            headers: {
                token:token
            }
        }, true)
            .then((data) => data.data)
            .catch((error) => {
                return thunkAPI.rejectWithValue(error)
            })
    }
);
export const fetchSpaces = createAsyncThunk(
    'getSpaces',
    async ({token, teamId}, thunkAPI) => {
        return await apiClient({
            url: `${Constants.API.BASE_URL}${Constants.API.FETCH_SPACES}`.replace("{teamId}", teamId),
            method: 'GET',
            headers: {
                token:token
            }
        }, true)
            .then(({data}) => {
                if (data && data.spaces && Array.isArray(data.spaces))
                    return {
                        [teamId]: data.spaces.map(t => {
                            return {id: t.id, name: t.name}
                        })
                    };
                else
                    return null;
            })
            .catch((error) => {
                return thunkAPI.rejectWithValue(error)
            })
    }
);
export const fetchFolders = createAsyncThunk(
    'getFolders',
    async ({token, spaceId}, thunkAPI) => {
        return await apiClient({
            url: `${Constants.API.BASE_URL}${Constants.API.FETCH_FOLDERS}`.replace("{spaceId}", spaceId),
            method: 'GET',
            headers: {
                token:token
            }
        }, true)
            .then(({data}) => {
                if (data && data.folders && Array.isArray(data.folders))
                    return {
                        [spaceId]: data.folders.map(t => {
                            return {id: t.id, name: t.name}
                        })
                    };
                else
                    return null;
            })
            .catch((error) => {
                return thunkAPI.rejectWithValue(error)
            })
    }
);

export const fetchLists = createAsyncThunk(
    'getLists',
    async ({token, folderId, spaceId = null}, thunkAPI) => {

        let url  = "";

        if(spaceId)
            url = `${Constants.API.BASE_URL}${Constants.API.FETCH_FOLDER_LESS_LISTS}`.replace("{spaceId}", spaceId);
        else
            url = `${Constants.API.BASE_URL}${Constants.API.FETCH_LISTS}`.replace("{folderId}", folderId);

        return await apiClient({
            url: url,
            method: 'GET',
            headers: {
                token:token
            }
        }, true)
            .then(({data}) => {
                if (data && data.lists && Array.isArray(data.lists))
                    return {
                        [folderId || spaceId]: data.lists.map(t => {
                            return {id: t.id, name: t.name}
                        })
                    };
                else
                    return null;
            })
            .catch((error) => {
                return thunkAPI.rejectWithValue(error)
            })
    }
);

const configSlice = createSlice({
    name: 'configs',
    initialState,
    reducers: {
        clearError(state, action) {
            state.error = null;
        },
        setConfigList(state, action) {
            state.configList = action.payload;
            setLocalData('configList', JSON.stringify(action.payload));
        }
    },
    extraReducers: (builder) => {
        /**Action--->get token */
        builder
            .addCase(fetchAccessToken.pending, (state, action) => {
                state.loading = true;
            })
            .addCase(fetchAccessToken.fulfilled, (state, action) => {
                state.error = null;
                state.loading = false;
                state.accessToken = action.payload.access_token;
                setLocalData('accessToken', JSON.stringify(action.payload.access_token));
            })
            .addCase(fetchAccessToken.rejected, (state, action) => {
                state.loading = false;
                toast.error(Constants.ERROR_MESSAGES.ERR_NETWORK);
            })
            .addCase(fetchTeams.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchTeams.fulfilled, (state, action) => {
                state.error = null;
                state.loading = false;
                state.teams = action.payload.teams;
                setLocalData('teams', JSON.stringify(action.payload.teams?.map(team => team.id) || []));
            })
            .addCase(fetchTeams.rejected, (state, action) => {
                state.loading = false;
                toast.error(Constants.ERROR_MESSAGES.ERR_NETWORK);
            })
            .addCase(fetchSpaces.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchSpaces.fulfilled, (state, action) => {
                state.error = null;
                state.loading = false;
                if (!Object.keys(state.spaces) && action.payload) {
                    state.spaces = action.payload;
                } else if (action.payload) {
                    state.spaces = {...state.spaces, ...action.payload};
                }
            })
            .addCase(fetchSpaces.rejected, (state, action) => {
                state.loading = false;
                toast.error(Constants.ERROR_MESSAGES.ERR_NETWORK);
            })
            .addCase(fetchFolders.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchFolders.fulfilled, (state, action) => {
                state.error = null;
                state.loading = false;
                if (!Object.keys(state.folders) && action.payload) {
                    state.folders = action.payload;
                } else if (action.payload) {
                    state.folders = {...state.folders, ...action.payload};
                }
            })
            .addCase(fetchFolders.rejected, (state, action) => {
                state.loading = false;
                toast.error(Constants.ERROR_MESSAGES.ERR_NETWORK);
            })
            .addCase(fetchLists.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchLists.fulfilled, (state, action) => {
                state.error = null;
                state.loading = false;
                if (!Object.keys(state.lists) && action.payload) {
                    state.lists = action.payload;
                } else if (action.payload) {
                    state.lists = {...state.lists, ...action.payload};
                }
            })
            .addCase(fetchLists.rejected, (state, action) => {
                state.loading = false;
                toast.error(Constants.ERROR_MESSAGES.ERR_NETWORK);
            })
    }
});


export const {clearError, setToken, setConfigList} = configSlice.actions;
export const getLoading = (state) => state.configs.loading;
export const getError = (state) => state.configs.error;
export const getTeams = (state) => state.configs.teams;
export const getSpaces = (state) => state.configs.spaces;
export const getFolders = (state) => state.configs.folders;
export const getLists = (state) => state.configs.lists;
export const getConfigList = (state) => state.configs.configList;
export const getAccessToken = (state) => state.configs.accessToken || JSON.parse(getLocalData('accessToken'));

export default configSlice.reducer;